// ─── State Inspector ──────────────────────────────────────────────────────────
//
// Read-only introspection into the Phase 3 state store.
// Provides structured reporting and per-key watching without any mutation.
//
// Does NOT own state - reads from appStateStore and stateRegistry.

import { appState } from '@/state/appStateStore';
import { stateRegistry } from '@/state/stateRegistry';
import { computedStateEngine } from '@/state/computedStateEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StateReport {
  rawKeys: string[];
  rawValues: Record<string, unknown>;
  derivedKeys: string[];
  totalEntries: number;
  capturedAt: number;
}

export interface KeyWatcher {
  key: string;
  unsubscribe: () => void;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Capture a snapshot of the current state store.
 * Returns raw keys, values, and derived key list.
 */
export function inspectState(): StateReport {
  const allValues = appState.getAllValues();
  const rawKeys = Object.keys(allValues);
  const rawValues: Record<string, unknown> = {};

  for (const key of rawKeys) {
    rawValues[key] = allValues[key];
  }

  // Derived keys come from the stateRegistry schema map
  const derivedKeys = stateRegistry.allKeys().filter(k =>
    computedStateEngine.isDerived(k)
  );

  return {
    rawKeys,
    rawValues,
    derivedKeys,
    totalEntries: rawKeys.length,
    capturedAt: Date.now(),
  };
}

/**
 * Returns the current value of a specific state key.
 * Returns undefined if the key is not in the store.
 */
export function getStateValue(key: string): unknown {
  try {
    return appState.get(key);
  } catch {
    return undefined;
  }
}

/**
 * Watch a specific state key for changes.
 * Returns a watcher object with an unsubscribe function.
 */
export function watchKey(key: string, cb: (value: unknown) => void): KeyWatcher {
  const unsub = appState.subscribe(key, (value) => {
    cb(value);
  });

  return { key, unsubscribe: unsub };
}

/**
 * Returns a formatted diagnostic string for a state report.
 */
export function formatStateReport(report: StateReport): string {
  const lines = [
    `State Report @ ${new Date(report.capturedAt).toISOString()}`,
    `  Total keys: ${report.totalEntries}`,
    `  Derived keys: ${report.derivedKeys.join(', ') || '(none)'}`,
    `  Values:`,
    ...report.rawKeys.map(k => `    ${k}: ${JSON.stringify(report.rawValues[k])}`),
  ];
  return lines.join('\n');
}
